import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { errorMessage } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';
import { GleeMark } from '@/components/Logo';
import { UI_VERSION, applyAccent, useBranding } from '@/lib/branding';

/**
 * Sign-in.
 *
 * A split layout: the form on the left at a comfortable reading width, and a
 * panel on the right that says what this system is. The panel is hidden below
 * `lg` — on a phone it would push the form off the fold, and the form is the
 * only thing anyone came here to use.
 */
export default function Login() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Branding is public: the login page has to render before anyone signs in.
  // Shared with the shell through one hook, so the name and colours somebody
  // sees on this page are the ones they see a second later inside.
  const branding = useBranding();
  const productName = branding.product_name;

  useEffect(() => {
    applyAccent(branding.accent, branding.accent_to);
  }, [branding.accent, branding.accent_to]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
      // The router swaps to the correct portal once `user` is set — a super
      // admin and a client user land in different applications.
    } catch (err) {
      setError(
        errorMessage(err, 'Could not sign in. Please try again.'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-full lg:grid-cols-[1fr_minmax(0,26rem)] xl:grid-cols-[1fr_minmax(0,32rem)]">
      {/* ---------------------------------------------------------------- */}
      {/* Form                                                              */}
      {/* ---------------------------------------------------------------- */}
      <div className="flex items-center justify-center bg-sunken px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={productName}
                className="mb-5 h-10 w-auto max-w-[14rem] object-contain object-left"
              />
            ) : (
              <GleeMark size={40} className="mb-5" />
            )}
            <h1 className="text-2xl font-semibold text-ink-900 dark:text-ink-50">
              Sign in
            </h1>
            <p className="mt-1.5 text-sm text-ink-500 dark:text-ink-400">
              Continue to your {productName} workspace.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
                className="animate-fade-in flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10A8 8 0 112 10a8 8 0 0116 0zm-8-4a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <Field label="Email" required>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                placeholder="you@company.com"
                autoFocus
                required
              />
            </Field>

            <Field label="Password" required>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••••••"
                required
              />
            </Field>

            <Button type="submit" loading={busy} className="mt-1 w-full">
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <div className="mt-5 flex items-center justify-between text-sm">
            <Link
              to="/forgot-password"
              className="text-ink-500 hover:text-brand-600 hover:underline dark:text-ink-400 dark:hover:text-brand-400"
            >
              Forgot your password?
            </Link>
            <Link
              to="/request-access"
              className="font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
              Request an account
            </Link>
          </div>

          <p className="mt-8 text-xs leading-relaxed text-ink-400 dark:text-ink-500">
            Access is monitored and audited. Repeated failed attempts lock the
            account.
          </p>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Context panel — decorative, and deliberately absent on mobile.    */}
      {/* ---------------------------------------------------------------- */}
      <aside className="relative hidden overflow-hidden bg-brand-900 lg:block">
        {/* Two soft radial washes rather than a flat fill: it gives the panel
            depth without an image to ship, load or get wrong in dark mode. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(60% 50% at 20% 10%, rgb(59 102 246 / 0.55), transparent 70%),' +
              'radial-gradient(50% 60% at 90% 90%, rgb(23 30 84 / 0.9), transparent 70%)',
          }}
        />
        <div className="relative flex h-full flex-col justify-between p-10 text-brand-50">
          <p className="text-sm font-medium tracking-[-0.005em] text-brand-100">
            {productName}
          </p>

          <div>
            <h2 className="max-w-sm text-3xl font-semibold leading-tight text-white">
              One AI voice agent for every home services vertical.
            </h2>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-brand-100/80">
              HVAC, roofing, plumbing, windows, solar and the rest — identified
              in conversation, qualified against your own rules, and transferred
              to a human the moment the lead is real.
            </p>

            <dl className="mt-10 grid max-w-sm grid-cols-3 gap-4 border-t border-white/15 pt-6">
              {[
                ['Verticals', '16+'],
                ['Dialers', 'Unlimited'],
                ['Handoff', 'Live transfer'],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-2xs font-semibold uppercase tracking-[0.06em] text-brand-200/70">
                    {label}
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-white">{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="space-y-1 text-xs text-brand-200/60">
            <p>The assistant identifies itself as automated on every call.</p>
            {/* The credit and the build, on the one page every operator and
                every client sees. */}
            <p>
              {branding.product_name}
              {branding.company_name && (
                <>
                  {' — powered by '}
                  {branding.company_url ? (
                    <a
                      href={branding.company_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="underline hover:text-white"
                    >
                      {branding.company_name}
                    </a>
                  ) : (
                    branding.company_name
                  )}
                </>
              )}
              {' · v'}
              {UI_VERSION}
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}
